// ============================================================================
// ADR-0024 — share capital and members (Corporate Facts)
// ============================================================================
// The year-end members are COMPUTED — the opening list (carried from last
// year's year end) plus the year's movements — never typed, so the trail
// always reconciles; a correction is itself a movement. Mirrors prima-api
// FactsMembers (the reports print the server's computation).

import type { MemberHolding, ShareMovement, ShareMovementKind } from 'api/types/fsa/corporateFacts.types';

export const KIND_LABEL: Record<ShareMovementKind, string> = {
  ALLOTMENT: 'Allotment',
  TRANSFER: 'Transfer',
  TRANSMISSION: 'Transmission',
  BUYBACK: 'Buy-back',
  FORFEITURE: 'Forfeiture',
};

/** Whether a kind takes shares from a member / gives them to one. */
export const kindTakes = (k: ShareMovementKind) => k !== 'ALLOTMENT';
export const kindGives = (k: ShareMovementKind) => k === 'ALLOTMENT' || k === 'TRANSFER' || k === 'TRANSMISSION';

export interface Member {
  id: string;
  folio?: string;
  name: string;
  promoter: boolean;
}

export interface ClosingHolding extends Member {
  class_id: string;
  shares: number;
}

const byDate = (a: ShareMovement, b: ShareMovement) => (a.date ?? '9999').localeCompare(b.date ?? '9999');

/** Everyone who is or becomes a member in the year: the opening list, then those a movement names. */
export function allMembers(opening: MemberHolding[], movements: ShareMovement[]): Member[] {
  const out: Member[] = [];
  const seen = new Set<string>();
  opening.forEach(h => {
    if (seen.has(h.local_id)) return;
    seen.add(h.local_id);
    out.push({ id: h.local_id, folio: h.folio, name: h.name, promoter: !!h.promoter });
  });
  [...movements].sort(byDate).forEach(m => {
    if (m.to_id || !m.to_name?.trim() || seen.has(m.local_id)) return;
    seen.add(m.local_id);
    out.push({ id: m.local_id, folio: m.to_folio, name: m.to_name.trim(), promoter: !!m.to_promoter });
  });
  return out;
}

/** The members at the year end (holders with shares left), in opening order then as they joined. */
export function closingMembers(opening: MemberHolding[], movements: ShareMovement[]): ClosingHolding[] {
  const members = allMembers(opening, movements);
  const index = new Map(members.map((m, i) => [m.id, i] as const));
  const shares = new Map<string, number>();
  const key = (member: string, cls: string) => `${member}\u0000${cls}`;
  const add = (member: string | null | undefined, cls: string, delta: number) => {
    if (!member || !index.has(member)) return;
    shares.set(key(member, cls), (shares.get(key(member, cls)) ?? 0) + delta);
  };
  opening.forEach(h => add(h.local_id, h.class_id, h.shares || 0));
  [...movements].sort(byDate).forEach(m => {
    const to = m.to_id ?? (m.to_name?.trim() ? m.local_id : null);
    if (kindTakes(m.kind)) add(m.from_id, m.class_id, -(m.shares || 0));
    if (kindGives(m.kind)) add(to, m.class_id, m.shares || 0);
  });
  const out: ClosingHolding[] = [];
  shares.forEach((n, k) => {
    if (n === 0) return;
    const [memberId, cls] = k.split('\u0000');
    const m = members[index.get(memberId)!];
    out.push({ ...m, class_id: cls, shares: n });
  });
  return out.sort((a, b) => index.get(a.id)! - index.get(b.id)! || a.class_id.localeCompare(b.class_id));
}

/** Members whose shares would go below zero (a transfer larger than the holding) — warned, not blocked. */
export function negativeHoldings(closing: ClosingHolding[]): ClosingHolding[] {
  return closing.filter(h => h.shares < 0);
}

/**
 * Pasted Excel rows → opening holdings. Columns (tab-separated): folio, name,
 * shares, promoter (Y/N, optional). A header row is skipped. All go to `classId`.
 */
export function parsePastedMembers(text: string, classId: string, newId: () => string): { rows: MemberHolding[]; skipped: number } {
  const rows: MemberHolding[] = [];
  let skipped = 0;
  text.split(/\r?\n/).forEach(line => {
    if (!line.trim()) return;
    const cells = line.split('\t').map(c => c.trim());
    const [folio, name, sharesText, promoterText] = cells.length >= 3 ? cells : ['', cells[0] ?? '', cells[1] ?? ''];
    const shares = Number((sharesText ?? '').replace(/,/g, ''));
    if (!name || !Number.isFinite(shares) || shares <= 0) { skipped++; return; }
    rows.push({
      local_id: newId(),
      folio: folio || undefined,
      name,
      class_id: classId,
      shares,
      promoter: /^(y|yes|true|1)$/i.test(promoterText ?? ''),
    });
  });
  return { rows, skipped };
}
